// e1c/src/components/content_editor/UnifiedProgramBuilder.js

import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Autocomplete, CircularProgress, Typography, Box, Alert, IconButton,
    Grid, Paper, List, ListItem, ListItemText, ListItemIcon, Chip, MenuItem, 
    FormGroup, FormControlLabel, Checkbox
} from '@mui/material';

// --- Icons ---
import CloseIcon from '@mui/icons-material/Close';
import HealingIcon from '@mui/icons-material/Healing';
import ArticleIcon from '@mui/icons-material/Article';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import authFetch from '../../utils/authFetch';
import logger from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';

const DAILY_CHECKIN_MAP = {
    vitals: 'Vitals',
    medication_adherence_check: 'Medication Log',
    symptoms_feelings: 'Symptoms',
    activity: 'Activity',
    diet_fluid: 'Diet & Fluids',
    journal_entry: 'Journal',
    mood: 'Mood / Feelings',
};

const PROGRAM_PHASE_OPTIONS = [
    { key: "onboarding", label: "Onboarding" },
    { key: "pre_surgery", label: "Pre-Surgery" },
    { key: "day_of_surgery", label: "Day of Surgery" },
    { key: "post_surgery", label: "Post-Surgery" },
    { key: "longitudinal", label: "Longitudinal" },
];
// --- Helper Component for Read-Only Content Lists ---
const BundleContentList = ({ title, items, icon }) => {
    if (!items || items.length === 0) return null;
    return (
        <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
            <List dense disablePadding>
                {items.map((item, index) => (
                    <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>{icon}</ListItemIcon>
                        <ListItemText 
                            primary={item.title} 
                            primaryTypographyProps={{ variant: 'body2' }} 
                            secondary={
                                (item.is_critical || item.is_attestation) ? (
                                    <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                        {item.is_critical && <Chip label="Critical" color="error" size="small" variant="outlined" sx={{height: 18, fontSize: '0.7rem'}} />}
                                        {item.is_attestation && <Chip label="Attestation" color="info" size="small" variant="outlined" sx={{height: 18, fontSize: '0.7rem'}} />}
                                    </Box>
                                ) : null
                            }
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

const UnifiedProgramBuilder = ({ open, onClose, tenantId, initialData = null }) => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    // --- State Management ---
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [programIdToEdit, setProgramIdToEdit] = useState(null);
    const [programDescription, setProgramDescription] = useState('');
    const [programJourneyType, setProgramJourneyType] = useState('ACUTE');
    const [phases, setPhases] = useState([]); 
    const [programHeroImage, setProgramHeroImage] = useState('');
    const [programWelcomeMessage, setProgramWelcomeMessage] = useState('');
    const [programClinicianName, setProgramClinicianName] = useState('');
    const [programClinicianUrl, setProgramClinicianUrl] = useState('');
    const [programClinicianPhone, setProgramClinicianPhone] = useState(''); 
    const [programClinicianTitle, setProgramClinicianTitle] = useState(''); 
    const [programClinicianAddress, setProgramClinicianAddress] = useState(''); 
    const [programClinicianEmail, setProgramClinicianEmail] = useState(''); 
    const [showHealthHub, setShowHealthHub] = useState(true);
    const [showRecoveryJournal, setShowRecoveryJournal] = useState(true);
    const [showGamification, setShowGamification] = useState(true);
    const [formError, setFormError] = useState('');

    // --- Data Fetching ---
    const { data: allPrograms = [], isLoading: isLoadingPrograms } = useQuery({
        queryKey: ['allPrograms', tenantId],
        queryFn: () => authFetch('/api/programs'),
        enabled: open,
    });

    const { data: allBundles = [], isLoading: isLoadingBundles } = useQuery({
        queryKey: ['allProtocols', tenantId],
        queryFn: () => authFetch('/api/protocols'),
        enabled: open,
        select: (data) => (Array.isArray(data) ? data.filter(p => p.protocol_type === 'care_bundle' && p.is_active) : []),
    });
    
    const { data: allContentItems = [] } = useQuery({
        queryKey: ['contentItemsForBuilder', tenantId],
        queryFn: () => authFetch('/api/content?status=active&latest_only=true&limit=500'),
        enabled: open,
    });

    useEffect(() => {
        logger.debug("UPB Effect: Running.");
        
        // --- START OF AUTO-FILL LOGIC FOR NEW PROGRAM ---
        const isCreatingNewProgram = typeof selectedProgram === 'string' && selectedProgram.trim() !== '';
        
        if (isCreatingNewProgram && programIdToEdit === null && user) {
            const teamProfile = user.team_member_profile;
            const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
            const displayName = teamProfile?.title ? `${teamProfile.title} ${user.last_name}` : fullName;
            
            logger.debug("UPB Effect: Detected new program creation. Auto-filling vCard fields from user profile.");
            
            // Auto-fill Clinician details from the logged-in user's profile
            setProgramClinicianName(displayName);
            setProgramClinicianTitle(teamProfile?.title || '');
            setProgramClinicianPhone(user.phone_number || '');
            setProgramClinicianEmail(user.email || '');
            // NOTE: Auto-filling the address line is complex, often best left blank unless a default location is stored on the user/tenant.
            setProgramClinicianAddress(''); 
            
            // Resetting other fields for new creation
            setProgramDescription('');
            setProgramJourneyType('ACUTE');
            setProgramHeroImage('');
            setProgramWelcomeMessage('');
            setProgramClinicianUrl('');
            setShowHealthHub(true);
            setShowRecoveryJournal(true);
            setShowGamification(true);
            setPhases([]); // Reset to empty array
        }
        // --- END OF AUTO-FILL LOGIC FOR NEW PROGRAM ---
        
        else if (selectedProgram) {
            if (typeof selectedProgram === 'object' && selectedProgram.id) {
                logger.debug("UPB Effect: Populating form from selected program object:", selectedProgram);
                setProgramIdToEdit(selectedProgram.id);
                setProgramDescription(selectedProgram.description || '');
                setProgramJourneyType(selectedProgram.journey_type || 'ACUTE');

                // --- NEW BRANDING FIELDS (Including the 4 fixed fields) ---
                setProgramHeroImage(selectedProgram.program_hero_image_url || '');
                setProgramWelcomeMessage(selectedProgram.program_welcome_message || '');
                setProgramClinicianName(selectedProgram.program_clinician_name || '');
                setProgramClinicianUrl(selectedProgram.program_clinician_url || '');
                
                // --- PULLING IN NEW FIELDS FROM DB ---
                setProgramClinicianPhone(selectedProgram.program_clinician_phone || ''); 
                setProgramClinicianTitle(selectedProgram.program_clinician_title || ''); 
                setProgramClinicianAddress(selectedProgram.program_clinician_address || ''); 
                setProgramClinicianEmail(selectedProgram.program_clinician_email || ''); 
                
                setShowHealthHub(selectedProgram.show_health_hub ?? true);
                setShowRecoveryJournal(selectedProgram.show_recovery_journal ?? true);
                setShowGamification(selectedProgram.show_gamification ?? true);

                if (selectedProgram.phases_definition && selectedProgram.phases_definition.length > 0) {
                    logger.debug("UPB Effect: Populating phases from new 'phases_definition'");
                    // Map bundle IDs from definition to full bundle objects
                    const populatedPhases = selectedProgram.phases_definition.map(phase => ({
                        ...phase,
                        bundle: allBundles.find(b => b.id === phase.bundle_id) || null
                    }));
                    setPhases(populatedPhases);

                } else {
                    logger.warn("UPB Effect: No 'phases_definition' found. Falling back to OLD static bundle fields.");

                    // My previous fallback logic was wrong. This is the correct logic.
                    // It maps the old static bundle IDs to the *correct, lowercase* phase keys.
                    const fallbackPhases = [
                        { ...(PROGRAM_PHASE_OPTIONS.find(p => p.key === 'onboarding')), bundle_id: selectedProgram.bundle_onboarding_id, bundle: allBundles.find(b => b.id === selectedProgram.bundle_onboarding_id) || null },
                        { ...(PROGRAM_PHASE_OPTIONS.find(p => p.key === 'pre_surgery')), bundle_id: selectedProgram.bundle_pre_surgery_id, bundle: allBundles.find(b => b.id === selectedProgram.bundle_pre_surgery_id) || null },
                        { ...(PROGRAM_PHASE_OPTIONS.find(p => p.key === 'day_of_surgery')), bundle_id: selectedProgram.bundle_day_of_surgery_id, bundle: allBundles.find(b => b.id === selectedProgram.bundle_day_of_surgery_id) || null },
                        { ...(PROGRAM_PHASE_OPTIONS.find(p => p.key === 'post_surgery')), bundle_id: selectedProgram.bundle_post_surgery_id, bundle: allBundles.find(b => b.id === selectedProgram.bundle_post_surgery_id) || null },
                        { ...(PROGRAM_PHASE_OPTIONS.find(p => p.key === 'longitudinal')), bundle_id: selectedProgram.bundle_longitudinal_id, bundle: allBundles.find(b => b.id === selectedProgram.bundle_longitudinal_id) || null },
                    ].filter(p => p.bundle_id || p.bundle); // Only keep phases that had a bundle
                    
                    setPhases(fallbackPhases);
                    // --- END OF EDITED BLOCK ---
                }
                
            } else if (typeof selectedProgram === 'string') {
                logger.debug("UPB Effect: selectedProgram is a string (new program name), resetting fields.");
                if (programIdToEdit === null) {
                    setProgramDescription('');

                    setProgramJourneyType('ACUTE');
                    setProgramHeroImage('');
                    setProgramWelcomeMessage('');
                    setProgramClinicianName('');
                    
                    setProgramClinicianUrl('');
                    setProgramClinicianPhone(''); // <--- NEW
                    setProgramClinicianTitle(''); // <--- NEW
                    setProgramClinicianAddress(''); // <--- NEW
                    setProgramClinicianEmail(''); // <--- NEW

                    setShowHealthHub(true);
                    setShowRecoveryJournal(true);
                    setShowGamification(true);
                    setPhases([]); 
                }
            }
        } else {
            logger.debug("UPB Effect: No selectedProgram, resetting fields.");
            setProgramIdToEdit(null); // <-- MAKE SURE TO RESET THIS
            setProgramDescription('');
            setProgramJourneyType('ACUTE');
            setProgramHeroImage('');
            setProgramWelcomeMessage('');
            setProgramClinicianName('');
            setProgramClinicianUrl('');
            
            // --- CLEAR NEW FIELDS ON FULL RESET ---
            setProgramClinicianPhone(''); 
            setProgramClinicianTitle(''); 
            setProgramClinicianAddress(''); 
            setProgramClinicianEmail(''); 
            
            setShowHealthHub(true); 
            setShowRecoveryJournal(true);
            setShowGamification(true);
            setPhases([]); 
        }
    }, [selectedProgram, initialData, allPrograms, allBundles, open, programIdToEdit, user]);
    const saveProgramMutation = useMutation({
        mutationFn: async (programPayload) => {
            const { isEdit, programData, shouldPropagate } = programPayload; 
        
            const payload = {
                name: programData.name,
                description: programData.description,
                journey_type: programData.journey_type,
                program_hero_image_url: programData.programHeroImage || null,
                program_welcome_message: programData.programWelcomeMessage || null,
                program_clinician_name: programData.programClinicianName || null,
                program_clinician_url: programData.programClinicianUrl || null,
                program_clinician_phone: programData.programClinicianPhone || null,
                program_clinician_title: programData.programClinicianTitle || null,
                program_clinician_address: programData.programClinicianAddress || null,
                program_clinician_email: programData.programClinicianEmail || null,

                phases_definition: programData.phases.map(phase => ({
                    key: phase.key,
                    label: phase.label,
                    bundle_id: phase.bundle?.id || null
                })),
                propagate_changes: shouldPropagate || false,
                bundle_onboarding_id: null,
                bundle_pre_surgery_id: null,
                bundle_day_of_surgery_id: null,
                bundle_post_surgery_id: null,
                bundle_longitudinal_id: null,
            };

            if (isEdit) {
                return authFetch(`/api/programs/${programData.id}`, { method: 'PATCH', body: payload });
            } else {
                const createPayload = {
                    ...payload,
                    key: programData.name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
                    is_active: true,
                };
                return authFetch('/api/programs', { method: 'POST', body: createPayload });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['programs', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['allPrograms', tenantId] });
            handleClose();
        },
        onError: (error) => {
            logger.error("Failed to save the program:", error);
            setFormError(error.detail || "An unexpected error occurred while saving.");
        },
    });

    // --- Event Handlers ---
    const handleClose = useCallback(() => {
        setFormError('');
        setSelectedProgram(null);
        setProgramIdToEdit(null);
        setPhases([]); // <-- RESET PHASES
        setProgramHeroImage(''); // <-- RESET BRANDING
        setProgramWelcomeMessage(''); // <-- RESET BRANDING
        setShowHealthHub(true);
        setShowRecoveryJournal(true);
        setShowGamification(true);
        onClose();
    }, [onClose]);
    
    // --- NEW PHASE HANDLERS ---
    const handlePhaseChange = (index, field, value) => {
        setPhases(currentPhases => {
            const newPhases = [...currentPhases];
            newPhases[index] = { ...newPhases[index], [field]: value };
            return newPhases;
        });
    };

    const handleAddPhase = () => {
        setPhases(currentPhases => [
            ...currentPhases,
            // --- START OF EDIT ---
            // Default to a valid lowercase key, not a placeholder
            { key: 'pre_surgery', label: 'New Phase', bundle: null }
            // --- END OF EDIT ---
        ]);
    };
    const handleRemovePhase = (index) => {
        setPhases(currentPhases => currentPhases.filter((_, i) => i !== index));
    };

    const handleMovePhase = (index, direction) => {
        setPhases(currentPhases => {
            const newPhases = [...currentPhases];
            const [movedPhase] = newPhases.splice(index, 1);
            newPhases.splice(index + direction, 0, movedPhase);
            return newPhases;
        });
    };
    
    const handleSave = (propagate = false) => { // <-- Accept flag
        const programName = typeof selectedProgram === 'object' ? selectedProgram.name : (selectedProgram || '');
        if (!programName.trim()) {
            setFormError("A Program Name is required.");
            return;
        }
        if (phases.length === 0) {
            setFormError("A program must have at least one phase.");
            return;
        }
        if (phases.some(p => !p.key || !p.key.trim() || !p.label || !p.label.trim())) {
            setFormError("All phases must have a non-empty Key and Label.");
            return;
        }

        const isEdit = !!programIdToEdit;
        
        saveProgramMutation.mutate({
            isEdit,
            shouldPropagate: propagate, 
            programData: {
                id: programIdToEdit,
                name: programName,
                description: programDescription,
                journey_type: programJourneyType,
                phases, 
                programHeroImage, 
                programWelcomeMessage, 
                programClinicianName,
                programClinicianUrl,
                programClinicianPhone, // <--- NEW
                programClinicianTitle, // <--- NEW
                programClinicianAddress, // <--- NEW
                programClinicianEmail, // <--- NEW
                show_health_hub: showHealthHub,
                show_recovery_journal: showRecoveryJournal,
                show_gamification: showGamification,
            },
        });
    };

    const isLoading = isLoadingPrograms || isLoadingBundles;
    const programNameValue = typeof selectedProgram === 'object' ? selectedProgram?.name : (selectedProgram || '');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Build / Edit Program
                <IconButton onClick={handleClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : (
                    <>
                        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                        <Grid container spacing={3} sx={{ mb: 2 }}> {/* Changed spacing to 3 */}
                            <Grid item xs={12}>
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    options={allPrograms}
                                    getOptionLabel={(option) => (typeof option === 'object' ? option.name : option) || ''}
                                    value={selectedProgram}
                                    onChange={(event, newValue) => {
                                        setSelectedProgram(newValue);
                                        // If this is a new program, clear the edit ID
                                        if (typeof newValue === 'string' || !newValue) {
                                            setProgramIdToEdit(null);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search to Edit, or Type to Create a New Program"
                                            helperText="Select an existing program to modify its phases, or type a new name to build from scratch."
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Program Description"
                                    value={programDescription}
                                    onChange={(e) => setProgramDescription(e.target.value)}
                                />
                            </Grid>

                            {/* --- VCard / CLINICIAN CONTACT FIELDS --- */} {/* <--- UPDATED HEADING */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 'bold' }}>Clinician / Care Team Contact Details (for Patient VCard)</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Clinician Display Name"
                                    value={programClinicianName}
                                    onChange={(e) => setProgramClinicianName(e.target.value)}
                                    helperText="e.g., 'Dr. Smith' (for FN field)"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Professional Title" // <--- NEW FIELD
                                    value={programClinicianTitle}
                                    onChange={(e) => setProgramClinicianTitle(e.target.value)}
                                    helperText="e.g., 'Chief Spine Surgeon'"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Contact Phone Number" // <--- NEW FIELD
                                    value={programClinicianPhone}
                                    onChange={(e) => setProgramClinicianPhone(e.target.value)}
                                    helperText="Patient support line (for TEL field)"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Contact Email" // <--- NEW FIELD
                                    value={programClinicianEmail}
                                    onChange={(e) => setProgramClinicianEmail(e.target.value)}
                                    helperText="Patient support email"
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Clinician/Image URL" // <--- UPDATED LABEL
                                    value={programHeroImage}
                                    onChange={(e) => setProgramHeroImage(e.target.value)}
                                    helperText="Clinician photo or organization logo (for PHOTO field)."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Clinician Profile URL"
                                    value={programClinicianUrl}
                                    onChange={(e) => setProgramClinicianUrl(e.target.value)}
                                    helperText="Homepage URL (for URL field)"
                                />
                            </Grid>
                            
                            <Grid item xs={12}> {/* Full-width address field */}
                                <TextField
                                    fullWidth
                                    label="Clinic Address Line" // <--- NEW FIELD
                                    value={programClinicianAddress}
                                    onChange={(e) => setProgramClinicianAddress(e.target.value)}
                                    helperText="Primary address line for the vCard (for ADR field)."
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Program Welcome Message"
                                    value={programWelcomeMessage}
                                    onChange={(e) => setProgramWelcomeMessage(e.target.value)}
                                    helperText="e.g., 'Welcome to the Spine Center' (for NOTE field)"
                                />
                            </Grid>
                        </Grid>
                        
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            Program Phase Playlists
                        </Typography>
                        <Box>
                            {phases.map((phase, index) => {
                                const selectedBundle = phase.bundle;
                                const bundleContents = selectedBundle?.module_definition;
                                
                                const medItems = bundleContents?.medication_directives?.map(d => ({ title: d.item_name || d.med_name, is_critical: d.is_critical || false })) || [];
                                
                                // [UPDATED LOGIC]
                                // Identify System Check-ins vs Normal Forms based on is_system_template metadata
                                const checkinItems = [];
                                const contentItems = [];
                                const trackerItems = []; // Custom Trackers

                                (bundleContents?.default_content_configs || []).forEach(config => {
                                    const item = allContentItems.find(ci => ci.id === config.content_item_id);
                                    if (!item) return;

                                    const displayItem = { 
                                        title: item.title, 
                                        is_critical: config.is_critical || false, 
                                        is_attestation: config.content_type === 'attestation_request' 
                                    };

                                    if (item.item_metadata?.is_system_template) {
                                        checkinItems.push(displayItem);
                                    } else if (item.content_type === 'activity_log_request') {
                                        trackerItems.push(displayItem);
                                    } else {
                                        contentItems.push(displayItem);
                                    }
                                });

                                // Legacy Support: Add old checkins string list if present
                                (bundleContents?.mandated_daily_checkins || []).forEach(key => {
                                    // Prevent duplicates if they were already added via default_content_configs
                                    if (!checkinItems.some(ci => ci.title === (DAILY_CHECKIN_MAP[key] || key))) {
                                        checkinItems.push({ title: DAILY_CHECKIN_MAP[key] || key, is_critical: false, is_attestation: false });
                                    }
                                });

                                return (
                                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, position: 'relative' }}>
                                    <Box sx={{position: 'absolute', top: 8, right: 8, display: 'flex', background: 'white', borderRadius: '4px', border: '1px solid #ddd'}}>
                                        <IconButton size="small" onClick={() => handleMovePhase(index, -1)} disabled={index === 0}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                                        <IconButton size="small" onClick={() => handleMovePhase(index, 1)} disabled={index === phases.length - 1}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                                        <IconButton size="small" onClick={() => handleRemovePhase(index)}><DeleteIcon fontSize="inherit" color="error" /></IconButton>
                                    </Box>

                                    <Grid container spacing={2} sx={{pr: '120px'}}> {/* Add padding to avoid overlap */}
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label={`Phase ${index + 1} Label`}
                                                value={phase.label}
                                                onChange={(e) => handlePhaseChange(index, 'label', e.target.value)}
                                                helperText="Human-readable tab name."
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                select // CHANGED from text
                                                fullWidth
                                                label={`Phase ${index + 1} Key`}
                                                value={phase.key} // This will now be 'pre_surgery'
                                                onChange={(e) => handlePhaseChange(index, 'key', e.target.value)}
                                                helperText="Unique system ID (e.g., pre_surgery)."
                                            >
                                                {PROGRAM_PHASE_OPTIONS.map((option) => (
                                                    <MenuItem key={option.key} value={option.key}>
                                                        {option.label} ({option.key})
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid item xs={12} sm={4}>
                                            <Autocomplete
                                                options={allBundles}
                                                getOptionLabel={(option) => option?.name || ''}
                                                value={selectedBundle}
                                                onChange={(e, v) => handlePhaseChange(index, 'bundle', v)}
                                                renderInput={(params) => <TextField {...params} label="Select Care Bundle" />}
                                            />
                                        </Grid>
                                    </Grid>
                                    
                                    {selectedBundle && (
                                        <Box sx={{ mt: 2, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                                            <BundleContentList title="Medications" items={medItems} icon={<HealingIcon fontSize="small"/>} />
                                            <BundleContentList title="Standard Check-ins" items={checkinItems} icon={<ChecklistIcon fontSize="small"/>} />
                                            <BundleContentList title="Custom Trackers" items={trackerItems} icon={<ChecklistIcon fontSize="small"/>} />
                                            <BundleContentList title="Materials & Forms" items={contentItems} icon={<ArticleIcon fontSize="small"/>} />
                                        </Box>
                                    )}
                                </Paper>
                            )})}
                            
                            <Button startIcon={<AddIcon />} onClick={handleAddPhase} variant="outlined" sx={{mt: 1}}>
                                Add Phase
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                
                {/* Standard Save (Safe Copy) */}
                <Button 
                    onClick={() => handleSave(false)} 
                    variant="outlined" 
                    disabled={saveProgramMutation.isPending || !programNameValue}
                >
                    Save Definition Only
                </Button>

                {/* Propagate Save (Overwrite & Sync) */}
                <Button 
                    onClick={() => handleSave(true)} 
                    variant="contained" 
                    disabled={saveProgramMutation.isPending || !programNameValue}
                >
                    {saveProgramMutation.isPending ? <CircularProgress size={24} /> : 'Save & Update Patients'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UnifiedProgramBuilder;